import React, {FC, useContext, useEffect, useState, useCallback, useLayoutEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {pdf} from '@react-pdf/renderer';
import {get} from 'lodash';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import {FormPageTypes} from 'appRedux/actions/forms/types';
import {RootReducer} from 'appRedux/reducers';
import {RequesterCaseItemType} from 'appRedux/actions/requestCase/types';
import {RequesterCaseCommentsTypes} from 'appRedux/actions/comments/types';

import {CryptoContext} from 'contexts/crypto/context';
import {CaseKeyContext} from 'contexts/caseKey/context';

import EmptyArea from 'components/EmptyArea';

import usePdfFileLoader from 'pages/agent/pdfExport/pdfPartials/usePdfFileLoader';
import PdfDocument from 'pages/agent/pdfExport/pdfPartials/PdfDocument';
import SectionsList from 'pages/agent/pdfExport/pdfForm/SectionsList';
import {getDecodedResults} from 'pages/client/form/helper';

import {decryptStringWithKey} from 'helpers/cryptoApiHelper';

import {theme} from 'config/theme';
import {getCurrentTemplates} from 'helpers/templatesHelper';
import AgentInverseButton from 'components/AgentScreenComponents/_buttons/AgentInverseButton';
import {REQUEST_CASE_EXPORT} from 'appRedux/actions/admin';

const buttonStyles = {
    width: 150,
    height: 40,
    borderRadius: 2,
    textTransform: 'none',
    backgroundColor: theme.palette.info.main,
    '&:hover': {
        backgroundColor: theme.palette.info.main,
    },
};

interface PdfEditorType {
    pages: FormPageTypes[];
}

export interface PrintSectionType {
    printSection: boolean;
    printComments?: boolean;
}

const PdfEditor: FC<PdfEditorType> = ({pages}) => {
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const {formId} = useParams();

    const {keys} = useContext(CryptoContext);
    const {encodedCaseKey, unwrappedCaseKey} = useContext(CaseKeyContext);

    const {
        requestCase: {currentCase},
        admin: {
            organizationPdfTemplates,
            languageList,
            formInfo: {translations},
        },
        profile: {profile},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const {files, signatures, comments, result, resultAgent, isEncryptInfo, popupItems} = currentCase;

    const orgLang = get(profile, 'lang', '');
    const languageId = get(profile, 'languageId', 1);
    const currentLang = languageList.find(item => item.id === languageId)?.name || '';

    const pdfTemplate = organizationPdfTemplates.find(template => currentCase.templatePdf === template.id);
    const templates = pdfTemplate?.attachments && getCurrentTemplates(pdfTemplate?.attachments, currentLang, orgLang);

    const exportCaseRequest = useCallback(
        data => dispatch({type: REQUEST_CASE_EXPORT.REQUEST, payload: data}),
        [dispatch],
    );
    const [printSections, setPrintSections] = useState<Record<string, Record<string, PrintSectionType>>>({});
    const [printAttachments, setPrintAttachments] = useState<Record<string, boolean>>({});
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

    const {loaded, fileImages, preparedSignatures, preparedPages, preparedTemplates, preparedPopupItems} =
        usePdfFileLoader({
            files,
            signatures,
            pages,
            templates,
            popupItems,
        });

    const onPageCheck = (pageId: number) => (checked: boolean) => {
        console.log('onPageCheck', pageId, checked);

        setPrintSections(prev => {
            const page = {};
            for (const sectionId in prev[pageId]) {
                page[sectionId] = {...prev[pageId][sectionId], printSection: checked};
            }
            const newState = {...prev, [pageId]: page};
            return newState;
        });
    };

    const onSectionCheck = (pageId: number, sectionId: number) => (print: PrintSectionType) => {
        console.log('onSectionCheck', pageId, sectionId, print);
        setPrintSections(prev => {
            const newState = {...prev, [pageId]: {...prev[pageId], [sectionId]: print}};
            return newState;
        });
    };

    const saveSettingsToLocalStorage = useCallback(
        (sections: Record<string, Record<string, PrintSectionType>>, attachments: Record<string, boolean>) => {
            console.log('saveSettingsToLocalStorage', JSON.stringify(sections));

            if (!formId) return;
            const settings = {
                sections,
                attachments,
            };
            localStorage.setItem(`pdf-preview-settings-${formId}-${pages[0].versionId}`, JSON.stringify(settings));
        },
        [formId],
    );

    const loadSettingsFromLocalStorage = useCallback(() => {
        if (!formId) return null;
        const settingsJson = localStorage.getItem(`pdf-preview-settings-${formId}-${pages[0].versionId}`);
        if (!settingsJson) return null;
        try {
            return JSON.parse(settingsJson);
        } catch (e) {
            console.error('Failed to parse saved settings', e);
            return null;
        }
    }, [formId]);

    const initializeSettings = useCallback(() => {
        if (!pages.length || !files) return;

        const savedSettings = loadSettingsFromLocalStorage();

        console.log('savedSettings', JSON.stringify(savedSettings));

        const completeDefaultSections = {};
        for (const page of pages) {
            completeDefaultSections[page.id] = {};
            for (const section of page.sections) {
                const hasComments = !!comments.find(comment => comment.section === section.id);
                completeDefaultSections[page.id][section.id] = {
                    printSection: true,
                    ...(hasComments ? {printComments: true} : {}),
                };
            }
        }

        if (savedSettings) {
            const mergedSections = {...completeDefaultSections};

            for (const pageId in savedSettings.sections) {
                if (mergedSections[pageId]) {
                    // Only if the page still exists
                    for (const sectionId in savedSettings.sections[pageId]) {
                        if (mergedSections[pageId][sectionId]) {
                            // Only if the section still exists
                            mergedSections[pageId][sectionId] = savedSettings.sections[pageId][sectionId];
                        }
                    }
                }
            }

            console.log('mergedSections', JSON.stringify(mergedSections));
            setPrintSections(mergedSections);

            const updatedAttachments = {...savedSettings.attachments};
            let hasNewFiles = false;

            for (const file of files) {
                if (updatedAttachments[file.uuid] === undefined) {
                    updatedAttachments[file.uuid] = true;
                    hasNewFiles = true;
                }
            }

            if (hasNewFiles) {
                setPrintAttachments(updatedAttachments);
            } else {
                setPrintAttachments(savedSettings.attachments);
            }
        } else {
            console.log('completeDefaultSections', JSON.stringify(completeDefaultSections));
            setPrintSections(completeDefaultSections);

            const defaultAttachments = {};
            for (const file of files) {
                defaultAttachments[file.uuid] = true;
            }
            setPrintAttachments(defaultAttachments);
        }
    }, [loadSettingsFromLocalStorage, pages, files, comments]);

    useLayoutEffect(() => {
        initializeSettings();
    }, [initializeSettings]);

    useEffect(() => {
        if (Object.keys(printSections).length === 0 && Object.keys(printAttachments).length === 0) {
            return;
        }

        saveSettingsToLocalStorage(printSections, printAttachments);
    }, [printSections, printAttachments, saveSettingsToLocalStorage]);

    const [clientInformation, setClientInformation] = useState<RequesterCaseItemType | null>(null);
    const [agentInformation, setAgentInformation] = useState<RequesterCaseItemType | null>(null);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (isEncryptInfo) {
                if (result && keys && encodedCaseKey) {
                    const currentResults = await getDecodedResults(result, encodedCaseKey, keys);
                    setClientInformation({
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        enc: isEncryptInfo,
                        ...currentResults,
                    });
                }
                if (resultAgent && keys && encodedCaseKey) {
                    const currentAgentResults = await getDecodedResults(resultAgent, encodedCaseKey, keys);
                    setAgentInformation({
                        enc: isEncryptInfo,
                        ...currentAgentResults,
                    });
                }
            } else {
                setClientInformation(result);
                setAgentInformation(resultAgent);
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [result, resultAgent, encodedCaseKey, isEncryptInfo]);

    const [commentsInfo, setCommentsInfo] = useState<RequesterCaseCommentsTypes[] | null>(null);

    useEffect(() => {
        if (!isEncryptInfo) {
            setCommentsInfo(comments);
            return;
        }

        const timer = setTimeout(async () => {
            if (comments && keys && unwrappedCaseKey) {
                const commentsRecord: RequesterCaseCommentsTypes[] = [];

                for (const comment of comments) {
                    const decodedMessage = await decryptStringWithKey(comment.text, unwrappedCaseKey);
                    commentsRecord.push({...comment, text: decodedMessage});
                }
                setCommentsInfo(commentsRecord);
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [isEncryptInfo, comments]);

    const generatePdf = useCallback(async () => {
        try {
            setIsGeneratingPdf(true);

            // Create the PDF blob
            const blob = await pdf(
                <PdfDocument
                    pages={preparedPages}
                    currentCase={{
                        ...currentCase,
                        comments: commentsInfo || [],
                        signatures: preparedSignatures,
                        popupItems: preparedPopupItems,
                    }}
                    result={clientInformation}
                    resultAgent={agentInformation}
                    formId={formId}
                    fileImages={fileImages}
                    printSections={printSections}
                    printAttachments={printAttachments}
                    translations={translations}
                    templates={preparedTemplates}
                />,
            ).toBlob();

            exportCaseRequest({id: currentCase.id});

            // Create a URL for the blob
            const url = URL.createObjectURL(blob);

            // Open in a new tab
            window.open(url, '_blank');

            // Clean up the URL object after a delay to ensure it's loaded
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGeneratingPdf(false);
        }
    }, [
        currentCase,
        printSections,
        printAttachments,
        preparedPages,
        preparedSignatures,
        preparedPopupItems,
        preparedTemplates,
        clientInformation,
        agentInformation,
        commentsInfo,
        fileImages,
        translations,
        formId,
        files,
    ]);

    const handleClickPreview = () => {
        saveSettingsToLocalStorage(printSections, printAttachments);
        setTimeout(() => generatePdf(), 200);
    };

    const areAllSectionsSelected = useCallback(() => {
        if (!pages || !pages.length) return true;

        for (const page of pages) {
            if (!printSections[page.id]) return false;

            for (const section of page.sections) {
                const sectionSettings = printSections[page.id][section.id];
                if (!sectionSettings || !sectionSettings.printSection) {
                    return false;
                }
            }
        }

        // Check if all attachments are selected
        if (files && files.length > 0) {
            for (const file of files) {
                if (!printAttachments[file.uuid]) {
                    return false;
                }
            }
        }

        return true;
    }, [pages, printSections, printAttachments, files]);

    return (
        <Box sx={{position: 'relative', pt: 16, display: 'flex', height: '100vh', maxWidth: '800px', mx: 'auto'}}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 3,
                }}
            >
                <Typography align="left" mb={2} sx={{fontSize: 32, fontWeight: 300}}>
                    {t('orguser.requesterCase.specifyExportedElements')}
                </Typography>

                <EmptyArea />

                <Button
                    sx={{...buttonStyles, mr: 1}}
                    onClick={handleClickPreview}
                    disabled={!loaded || isGeneratingPdf || !commentsInfo || !Object.keys(printSections).length}
                >
                    <Typography sx={{color: theme.palette.background.default}}>
                        {isGeneratingPdf
                            ? t('common.registrationForm.loadingData')
                            : t('orguser.requesterCase.generatePdf')}
                    </Typography>
                </Button>
            </Box>
            <Box sx={{position: 'absolute', top: 88, left: 0, display: 'flex', flexDirection: 'column', gap: 1}}>
                {!areAllSectionsSelected() && (
                    <AgentInverseButton
                        onClick={() => {
                            // Select all sections
                            const newPrintSections = {};
                            for (const page of pages) {
                                newPrintSections[page.id] = {};
                                for (const section of page.sections) {
                                    const hasComments = !!comments.find(comment => comment.section === section.id);
                                    newPrintSections[page.id][section.id] = {
                                        printSection: true,
                                        ...(hasComments ? {printComments: true} : {}),
                                    };
                                }
                            }
                            setPrintSections(newPrintSections);

                            // Select all attachments
                            const newPrintAttachments = {};
                            if (files) {
                                for (const file of files) {
                                    newPrintAttachments[file.uuid] = true;
                                }
                                setPrintAttachments(prev => ({...prev, ...newPrintAttachments}));
                            }
                        }}
                        title={t('orguser.requesterCase.topbar.selectAll')}
                    />
                )}
            </Box>

            <Box sx={{overflowY: 'auto', flex: 1, py: 1, px: 2}}>
                {/* Only render pages when printSections is properly initialized */}
                {pages && Object.keys(printSections).length > 0 ? (
                    pages.map((page: FormPageTypes) => {
                        // Only render this page if it has a corresponding record in printSections
                        if (!printSections[page.id]) return null;

                        return (
                            <SectionsList
                                page={page}
                                currentCase={currentCase}
                                key={page.id}
                                formId={formId}
                                printSections={printSections[page.id]}
                                onSectionCheck={onSectionCheck}
                                onPageCheck={onPageCheck}
                                printAttachments={printAttachments}
                                setPrintAttachments={setPrintAttachments}
                            />
                        );
                    })
                ) : (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                        <Typography>{t('common.registrationForm.loadingData')}</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PdfEditor;
